/* eslint-disable */
import { useEffect } from 'react';
import { Button } from '@/Components/ui/button';
import { X, Loader2 } from 'lucide-react';

const ConfirmationModal = ({
	isOpen,
	onClose = () => { },
	onConfirm = () => { },
	message = '',
	heading = '',
	isAlert = false,
	alertType = 'info',
	textAlign = false,
	showCrossBtn = false,
	customButton = [],
	isLoading = false,
	width = 'w-96 max-w-md',
}) => {

	useEffect(() => {
		let timer;
		if (isOpen && isAlert && (!onConfirm || showCrossBtn)) {
			timer = setTimeout(() => {
				onClose();
			}, 10000);
		}
		return () => {
			if (timer) clearTimeout(timer);
		};
	}, [isOpen, isAlert, onConfirm, showCrossBtn, onClose]);

	if (!isOpen) return null;

	// Alert styles based on type
	const getAlertStyles = () => {
		switch (alertType) {
			case 'success':
				return {
					bg: 'bg-green-50 border-green-200',
					text: 'text-green-800',
					icon: 'text-green-600',
					button: 'bg-green-600 hover:bg-green-700',
				};
			case 'warning':
				return {
					bg: 'bg-yellow-50 border-yellow-200',
					text: 'text-yellow-800',
					icon: 'text-yellow-600',
					button: 'bg-yellow-600 hover:bg-yellow-700',
				};
			case 'error':
				return {
					bg: 'bg-red-50 border-red-200',
					text: 'text-red-800',
					icon: 'text-red-600',
					button: 'bg-red-600 hover:bg-red-700',
				};
			default:
				return {
					bg: 'bg-blue-50 border-blue-200',
					text: 'text-blue-800',
					icon: 'text-blue-600',
					button: 'bg-blue-600 hover:bg-blue-700',
				};
		}
	};

	const alertStyles = getAlertStyles();

	if (isAlert) {
		// Alert box mode
		return (
			<div
				className={`fixed top-4 right-4 z-[999999] max-w-sm w-full ${alertStyles.bg} border ${alertStyles.bg.split('-')[0]}-200 rounded-lg shadow-lg p-4 transition-all duration-300 ease-in-out`}
				style={{
					animation: 'slideInRight 0.3s ease-out',
				}}
			>
				<div className='flex items-start gap-3'>
					<div className={`flex-shrink-0 ${alertStyles.icon}`}>
						{/* Alert icon based on type */}
						{alertType === 'success' && (
							<svg className='w-5 h-5' fill='currentColor' viewBox='0 0 20 20'>
								<path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z' clipRule='evenodd' />
							</svg>
						)}
						{alertType === 'warning' && (
							<svg className='w-5 h-5' fill='currentColor' viewBox='0 0 20 20'>
								<path fillRule='evenodd' d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z' clipRule='evenodd' />
							</svg>
						)}
						{alertType === 'error' && (
							<svg className='w-5 h-5' fill='currentColor' viewBox='0 0 20 20'>
								<path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z' clipRule='evenodd' />
							</svg>
						)}
						{alertType === 'info' && (
							<svg className='w-5 h-5' fill='currentColor' viewBox='0 0 20 20'>
								<path fillRule='evenodd' d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z' clipRule='evenodd' />
							</svg>
						)}
					</div>
					<div className='flex-1'>
						<h3 className={`text-sm font-medium ${alertStyles.text} mb-1`}>
							{heading || 'Notification'}
						</h3>
						<p className={`text-sm ${alertStyles.text} opacity-90`}>
							{message || 'This is an alert message.'}
						</p>
					</div>
					{showCrossBtn && <button
						onClick={onClose}
						className={`flex-shrink-0 ${alertStyles.text} hover:opacity-70 transition-opacity`}
					>
						<svg className='w-4 h-4' fill='currentColor' viewBox='0 0 20 20'>
							<path fillRule='evenodd' d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z' clipRule='evenodd' />
						</svg>
					</button>}
				</div>
				{onConfirm && !showCrossBtn && (
					<div className='flex justify-end gap-2 mt-3 pt-3 border-t border-gray-200'>
						<Button
							variant='secondary'
							onClick={onClose}
							className='px-3 py-1 text-xs'
							disabled={isLoading}
						>
							Cancel
						</Button>
						<Button
							onClick={onConfirm}
							className={`px-3 py-1 text-xs ${alertStyles.button}`}
							disabled={isLoading}
						>
							{isLoading && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
							Confirm
						</Button>
					</div>
				)}
			</div>
		);
	}

	return (
		<div
			className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center'
			style={{
				zIndex: 999999,
			}}
		>
			<div className={`bg-white rounded-lg p-6 ${width} shadow-2xl`}>
				<div className='mb-4'>
					<div className={`flex items-center w-full ${showCrossBtn ? 'justify-between' : 'justify-center'}`}>
						<h3 className='text-lg font-semibold text-gray-900 mb-2'>
							{heading || 'Confirm Action'}
						</h3>
						{showCrossBtn && <p className='flex justify-end'> <X size={20} onClick={onClose} className='cursor-pointer text-gray-800' /> </p>}
					</div>
					<p className={`text-sm text-gray-600 ${textAlign ? 'text-left' : ''}`}>
						{message || 'Are you sure you want to proceed?'}
					</p>
				</div>
				<div className='flex justify-end gap-3'>
					{customButton.length > 0 ? customButton?.map((item) => (
						<Button
							variant='secondary'
							onClick={item?.onClick}
							className={`px-4 py-2 ${item?.className}`}
							disabled={item?.disabled ?? isLoading}
						>
							{item?.label}
						</Button>
					)) : (
						<>
							<Button
								variant='secondary'
								onClick={onClose}
								className='px-4 py-2'
								disabled={isLoading}
							>
								Cancel
							</Button>
							<Button
								onClick={onConfirm}
								className='px-4 py-2 bg-blue-600 hover:bg-blue-700'
								disabled={isLoading}
							>
								{isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
								Confirm
							</Button>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default ConfirmationModal;
